import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdArrowBack, MdChevronRight, MdAssignment, MdOpenInNew, MdPending, MdCheckCircle } from 'react-icons/md';
import { CiMenuKebab } from "react-icons/ci";
import { useLanguage } from '../../context/LanguageContext';

const AgentDetail = () => {
  const { t } = useLanguage();
  const { id } = useParams(); // Bu artık UUID olacak
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    records_per_page: 10
  });
  const [statistics, setStatistics] = useState({
    total_cases: 0,
    open_cases: 0,
    in_progress_cases: 0,
    resolved_cases: 0,
    closed_cases: 0
  });
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    showCancel: true
  });
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    agent_status: true,
    full_name: '',
    username: '',
    password_hash: '',
    agent_type: 'Agent',
    department_id: '',
    agent_email: '',
    agent_gsm: '',
    company_id: null
  });
  const [departments, setDepartments] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDepartments();
  }, [id]);

  // URL'de edit parametresi varsa edit formunu aç
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editParam = params.get('edit');
    if (editParam === 'true' && agent) {
      handleEdit();
    }
  }, [agent]);

  const fetchData = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await api.get(`/agents/${id}?page=${page}&limit=${limit}`);
      if (response.data.success) {
        setAgent(response.data.agent);
        setPagination(response.data.pagination);
        // Statistics verilerini güvenli bir şekilde ayarla
        const stats = response.data.statistics || {};
        setStatistics({
          total_cases: stats.total_cases || 0,
          open_cases: stats.open_cases || 0,
          in_progress_cases: stats.in_progress_cases || 0,
          resolved_cases: stats.resolved_cases || 0,
          closed_cases: stats.closed_cases || 0
        });
      }
         } catch (error) {
       console.error('Veri yüklenirken hata:', error);
       showConfirmPopup({
         title: t('common.error'),
         type: 'error',
         message: t('agentDetail.loadError'),
         showCancel: false
       });
     } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage, pagination?.records_per_page || 10);
  };

  const handlePageSizeChange = (newSize) => {
    fetchData(1, newSize);
  };

  // Departmanları getir
  const fetchDepartments = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        const response = await api.get(`/departments/${userData.companyId}`);
        setDepartments(response.data.departments);
      }
    } catch (error) {
      console.error('Departmanlar yüklenirken hata:', error);
    }
  };

  // Popup fonksiyonları
  const showConfirmPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Edit fonksiyonu - Form verilerini hazırla ve popup'ı aç
  const handleEdit = () => {
    setEditFormData({
      agent_status: agent.agent_status === 1,
      full_name: agent.full_name,
      username: agent.username,
      password_hash: '',
      agent_type: agent.agent_type,
      department_id: agent.department_id,
      agent_email: agent.agent_email,
      agent_gsm: agent.agent_gsm,
      company_id: agent.company_id
    });
    setIsEditing(true);
    setShowEditForm(true);
  };

  // Form değişikliklerini handle et
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Form submit işlemi
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = { ...editFormData };
      
      if (isEditing && !submitData.password_hash) {
        delete submitData.password_hash;
      }

      const response = await api.put(`/agents/${agent.id}`, submitData);
      
      if (response.data.success) {
        setShowEditForm(false);
        setIsEditing(false);
        fetchData(); // Verileri yenile
        showConfirmPopup({
          title: t('common.success'),
          message: t('agentDetail.agentUpdated'),
          type: 'success',
          showCancel: false,
          onConfirm: () => {
            closePopup();
          }
        });
      }
    } catch (error) {
      showConfirmPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('agentDetail.updateError'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };

  // Delete fonksiyonu
  const handleDelete = async () => {
    const agentIdentifier = agent.company_agent_uuid || agent.id;
    
    showConfirmPopup({
      title: t('agentDetail.deleteAgent'),
      message: t('agentDetail.deleteConfirm', { name: agent.full_name }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/agents/${agentIdentifier}`);
          if (response.data.success) {
            showConfirmPopup({
              title: t('common.success'),
              message: t('agentDetail.agentDeleted', { name: agent.full_name }),
              type: 'success',
              showCancel: false,
              onConfirm: () => {
                closePopup();
                navigate('/settings/agent');
              }
            });
          }
        } catch (error) {
          showConfirmPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('agentDetail.deleteError'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (!agent) return <div>{t('agentDetail.agentNotFound')}</div>;
  if (!pagination) return <div>{t('common.loading')}</div>;

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center text-sm mb-6 pt-4">
        <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
          <Link 
            to="/settings/agent" 
            className="text-[#fff] hover:text-[gray] font-medium transition-colors duration-200"
          >
            {t('settings.title')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff]" size={20} />
          <Link 
            to="/settings/agent" 
            className="text-[#fff] hover:text-[gray] font-medium transition-colors duration-200"
          >
            {t('settings.agentSettings')}
          </Link>
          <MdChevronRight className="mx-2 text-[#fff]" size={20} />
          <span className="text-[#fff] font-medium">
            {agent.full_name}
          </span>
        </div>
      </nav>

      {/* Main Content */}
      <div className="space-y-6">
                 {/* Agent Details Card */}
                   <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
              <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('agentDetail.agentDetails')}</h1>
              <div className="relative">
                {/* Actions Menu Button */}
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition-colors"
                  title="Actions"
                >
                  <CiMenuKebab size={20} />
                </button>
                
                {/* Actions Dropdown Menu */}
                {showActionsMenu && (
                  <div className="absolute right-0 top-10 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          handleEdit();
                          setShowActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdEdit size={16} />
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => {
                          handleDelete();
                          setShowActionsMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <MdDelete size={16} />
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
           </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                   <tr>
                     <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.fullName')}</td>
                     <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.full_name}</td>
                     <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.username')}</td>
                     <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.username}</td>
                   </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.status')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        agent.agent_status 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                      }`}>
                        {agent.agent_status ? t('agentDetail.active') : t('agentDetail.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.agentType')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.agent_type}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.department')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.department_name}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.email')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.agent_email}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.gsm')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{agent.agent_gsm}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.lastLogin')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                      {agent.last_login ? new Date(agent.last_login).toLocaleString() : '-'}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.createdAt')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(agent.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('agentDetail.updatedAt')}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-white">{new Date(agent.updated_at).toLocaleDateString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {/* Full Name & Username */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.fullName')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.full_name}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.username')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.username}</div>
                </div>
              </div>

              {/* Status & Agent Type */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.status')}</div>
                  <div className="text-gray-900 dark:text-white">
                    <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                      agent.agent_status 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                    }`}>
                      {agent.agent_status ? t('agentDetail.active') : t('agentDetail.inactive')}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.agentType')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.agent_type}</div>
                </div>
              </div>

              {/* Department & Email */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.department')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.department_name}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.email')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.agent_email}</div>
                </div>
              </div>

              {/* GSM & Last Login */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.gsm')}</div>
                  <div className="text-gray-900 dark:text-white">{agent.agent_gsm}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.lastLogin')}</div>
                  <div className="text-gray-900 dark:text-white">
                    {agent.last_login ? new Date(agent.last_login).toLocaleString() : '-'}
                  </div>
                </div>
              </div>

              {/* Created At & Updated At */}
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.createdAt')}</div>
                  <div className="text-gray-900 dark:text-white">{new Date(agent.created_at).toLocaleDateString()}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.updatedAt')}</div>
                  <div className="text-gray-900 dark:text-white">{new Date(agent.updated_at).toLocaleDateString()}</div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Assigned Support Cases Card */}
                 <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
           <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg flex justify-between items-center">
             <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('agentDetail.assignedSupportCases')}</h2>
                         <div className="flex items-center gap-2">
               <span className="text-sm text-gray-600 dark:text-gray-400">{t('common.show')}</span>
               <select
                 value={pagination?.records_per_page || 10}
                 onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                 className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
               >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('table.entries')}</span>
            </div>
          </div>
          <div className="overflow-hidden">
            {/* Desktop Table View */}
            <div className="hidden lg:block">
              <div className="overflow-x-auto">
                               <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                   <thead className="bg-gray-50 dark:bg-gray-700">
                     <tr>
                       <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('agentDetail.caseId')}</th>
                       <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('agentDetail.customer')}</th>
                       <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('agentDetail.subject')}</th>
                       <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('agentDetail.status')}</th>
                       <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('agentDetail.createdDate')}</th>
                     </tr>
                   </thead>
                                   <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                     {!agent.assigned_cases || agent.assigned_cases.length === 0 ? (
                       <tr>
                         <td colSpan="5" className="px-4 py-2 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                           {t('agentDetail.noSupportCases')}
                         </td>
                       </tr>
                     ) : (
                       agent.assigned_cases.map((supportCase, index) => {
                         const isLast = index === agent.assigned_cases.length - 1;
                         return (
                           <tr key={supportCase.id}>
                             <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                               #{supportCase.case_number}
                             </td>
                             <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{supportCase.customer_name}</td>
                             <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{supportCase.case_subject}</td>
                             <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                               <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                                 supportCase.case_status === 'Open' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' :
                                 supportCase.case_status === 'In Progress' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300' :
                                 supportCase.case_status === 'Resolved' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' :
                                 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300'
                               }`}>
                                 {supportCase.case_status}
                               </span>
                             </td>
                             <td className={`px-4 py-2 text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>
                               {new Date(supportCase.created_at).toLocaleDateString()}
                             </td>
                           </tr>
                         );
                       })
                     )}
                   </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4 p-4">
              {!agent.assigned_cases || agent.assigned_cases.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 dark:text-gray-500 text-sm">{t('agentDetail.noSupportCases')}</p>
                </div>
              ) : (
                agent.assigned_cases.map((supportCase, index) => (
                  <div key={supportCase.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                    {/* Case ID & Customer */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.caseId')}</div>
                        <div className="text-sm text-gray-900 dark:text-white font-medium">#{supportCase.case_number}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.customer')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{supportCase.customer_name}</div>
                      </div>
                    </div>

                    {/* Subject & Status */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.subject')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">{supportCase.case_subject}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.status')}</div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                            supportCase.case_status === 'Open' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' :
                            supportCase.case_status === 'In Progress' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-300' :
                            supportCase.case_status === 'Resolved' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' :
                            'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-300'
                          }`}>
                            {supportCase.case_status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Created Date */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('agentDetail.createdDate')}</div>
                      <div className="text-sm text-gray-900 dark:text-white">{new Date(supportCase.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

                     {/* Pagination */}
           {pagination?.total_pages > 1 && (
             <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
               <div className="flex items-center justify-between">
                 <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                   {t('table.showing')} {((pagination.current_page - 1) * pagination.records_per_page) + 1} {t('table.to')} {Math.min(pagination.current_page * pagination.records_per_page, pagination.total_records)} {t('table.of')} {pagination.total_records} {t('table.entries')}
                 </div>
                 <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                   <button
                     onClick={() => handlePageChange(pagination.current_page - 1)}
                     disabled={pagination.current_page === 1}
                     className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                       pagination.current_page === 1
                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                         : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                     }`}
                   >
                     {t('table.previous')}
                   </button>
                   {[...Array(pagination.total_pages)].map((_, index) => (
                     <button
                       key={index + 1}
                       onClick={() => handlePageChange(index + 1)}
                       className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                         pagination.current_page === index + 1
                           ? 'bg-[#272c39] text-white'
                           : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                       }`}
                     >
                       {index + 1}
                     </button>
                   ))}
                   <button
                     onClick={() => handlePageChange(pagination.current_page + 1)}
                     disabled={pagination.current_page === pagination.total_pages}
                     className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                       pagination.current_page === pagination.total_pages
                         ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                         : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                     }`}
                   >
                     {t('table.next')}
                   </button>
                 </nav>
               </div>
             </div>
           )}
        </div>

        {/* Statistics Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
            <h2 className="text-lg font-medium text-gray-800 dark:text-white">{t('agentDetail.supportCaseStatistics')}</h2>
          </div>
          <div className="p-6">
                         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                                           {/* Total Cases */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                        <MdAssignment className="text-white" size={24} />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentDetail.totalCases')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics?.total_cases || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Open Cases */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                        <MdOpenInNew className="text-white" size={24} />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentDetail.openCases')}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics?.open_cases || 0}</p>
                    </div>
                  </div>
                </div>

                               {/* In Progress Cases */}
                 <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                   <div className="flex items-center">
                     <div className="flex-shrink-0">
                       <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                         <MdPending className="text-white" size={24} />
                       </div>
                     </div>
                     <div className="ml-4">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentDetail.inProgress')}</p>
                       <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics?.in_progress_cases || 0}</p>
                     </div>
                   </div>
                 </div>

                 {/* Closed Cases */}
                 <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                   <div className="flex items-center">
                     <div className="flex-shrink-0">
                       <div className="w-12 h-12 bg-gray-500 rounded-lg flex items-center justify-center">
                         <MdCheckCircle className="text-white" size={24} />
                       </div>
                     </div>
                     <div className="ml-4">
                       <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('agentDetail.closed')}</p>
                       <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{statistics?.closed_cases || 0}</p>
                     </div>
                   </div>
                 </div>
            </div>
          </div>
        </div>
      </div>

                           {/* Popup Component */}
        {popup.show && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
            <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">{popup.title}</h3>
              </div>
              
              <div className="p-8 bg-white dark:bg-gray-800">
                <div className="text-center">
                  <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                    {popup.message}
                  </p>
                  <div className="flex justify-center gap-4">
                    {popup.showCancel && (
                      <button
                        onClick={closePopup}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                      >
                        {t('common.cancel')}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (popup.onConfirm) {
                          popup.onConfirm();
                        } else {
                          closePopup();
                        }
                      }}
                      className={`px-4 py-2 text-white rounded-md ${
                        popup.type === 'error' 
                          ? 'bg-red-600 hover:bg-red-700'
                          : popup.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-[#272c39] hover:bg-[#1e2230]'
                      }`}
                    >
                      {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Form Popup */}
        {showEditForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9998] flex items-center justify-center">
            <div className="relative mx-auto w-[900px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
              {/* Header */}
              <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-medium">
                  {t('agentDetail.editAgent')}
                </h3>
                <button
                  onClick={() => setShowEditForm(false)}
                  className="text-gray-300 hover:text-white"
                >
                  <MdClose size={24} />
                </button>
              </div>
              
              {/* Form Content */}
              <div className="p-6">
                <form onSubmit={handleFormSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.fullName')} *
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        value={editFormData.full_name}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.username')} *
                      </label>
                      <input
                        type="text"
                        name="username"
                        value={editFormData.username}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.passwordLeaveEmpty')}
                      </label>
                      <input
                        type="password"
                        name="password_hash"
                        value={editFormData.password_hash}
                        onChange={handleFormChange}
                        autoComplete="new-password"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Agent Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.agentType')} *
                      </label>
                      <select
                        name="agent_type"
                        value={editFormData.agent_type}
                        onChange={handleFormChange}
                        required
                        disabled={(() => {
                          try {
                            const userStr = localStorage.getItem('user');
                            if (!userStr) return true;
                            const user = JSON.parse(userStr);
                            return !(user.agent_type === 'Admin' || user.agent_type === 'Owner');
                          } catch {
                            return true;
                          }
                        })()}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Admin">Admin</option>
                        <option value="Agent">Agent</option>
                      </select>
                    </div>

                    {/* Department */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.department')}
                      </label>
                      <select
                        name="department_id"
                        value={editFormData.department_id}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">{t('agentDetail.selectDepartment')}</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.department_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.email')} *
                      </label>
                      <input
                        type="email"
                        name="agent_email"
                        value={editFormData.agent_email}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* GSM */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('agentDetail.gsm')}
                      </label>
                      <input
                        type="text"
                        name="agent_gsm"
                        value={editFormData.agent_gsm}
                        onChange={handleFormChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Status */}
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="agent_status"
                          checked={editFormData.agent_status}
                          onChange={handleFormChange}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('agentDetail.active')}</span>
                      </label>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="col-span-2 flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowEditForm(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                    >
                      {t('agentDetail.updateAgent')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default AgentDetail; 